import { useState, useEffect, useRef } from "react";
import { NavLink, Link } from "react-router-dom";
import logo_selah from "assets/img/selah.png";
import { manualScrollRef } from "components/bible/BookChapters";
import LastReadButton from "components/navigation/LastReadButton";

function Navbar({ scrollContainerRef = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current || window;

    const handleScroll = () => {
      if (manualScrollRef.current) return;

      const currentScrollY =
        scrollContainer === window ? window.scrollY : scrollContainer.scrollTop;

      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        if (showNavbar) setShowNavbar(false);
      } else {
        if (!showNavbar) setShowNavbar(true);
      }

      lastScrollY.current = currentScrollY;
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [showNavbar, scrollContainerRef]);

  const links = [
    { path: "buscador", label: "Buscador" },
    { path: "biblia", label: "Biblia" },
    { path: "planlectura", label: "Planes de Lectura" },
    { path: "libros", label: "Libros Recomendados" },
  ];

  const linkClasses = ({ isActive }) =>
    `relative block font-medium text-gray-700 font-bembo font-semibold hover:text-gray-900
     transition duration-300 ease-in-out ${
       isActive ? "text-gray-900 after:scale-x-100" : ""
     }`;

  const underline =
    "after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-full after:bg-gradient-to-r after:from-gray-400 after:to-gray-600 after:scale-x-0 after:transition after:origin-left hover:after:scale-x-100";

  return (
    <nav
      id="navbar"
      role="navigation"
      aria-label="Main Navigation"
      className={`w-full fixed top-0 z-50 py-3 sm:py-4 lg:py-5 bg-gray-50 shadow
      transition-transform duration-300 ease-in-out ${
        showNavbar ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between px-4 sm:px-6">
        {/* Logo Link */}
        <Link to="/" className="flex items-center" aria-label="Inicio">
          <img
            src={logo_selah}
            alt="Logo de Selah"
            className="h-12 sm:h-14 lg:h-16 w-auto transition-transform duration-300 ease-in-out hover:scale-105"
          />
        </Link>

        {/* Hamburger Button */}
        <button
          type="button"
          aria-label="Alternar menú de navegación"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden flex flex-col justify-center items-center space-y-1.5 focus:outline-none relative w-9 h-9"
        >
          <span
            className={`block w-8 h-0.5 bg-gray-600 rounded transition transform ${
              isOpen ? "rotate-45 translate-y-2" : ""
            }`}
          />
          <span
            className={`block w-8 h-0.5 bg-gray-600 rounded transition ${
              isOpen ? "opacity-0" : "opacity-100"
            }`}
          />
          <span
            className={`block w-8 h-0.5 bg-gray-600 rounded transition transform ${
              isOpen ? "-rotate-45 -translate-y-2" : ""
            }`}
          />
        </button>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex space-x-6 lg:space-x-10 items-center">
          {links.map(({ path, label }) => (
            <NavLink
              key={path}
              to={`/${path}`}
              className={(props) =>
                linkClasses(props) + " " + underline + " text-base lg:text-lg"
              }
            >
              {label}
            </NavLink>
          ))}

          {/* LAST READ BUTTON */}
          <div className="pl-2">
            <LastReadButton className={""} />
          </div>
        </div>
      </div>

      {/* Mobile Nav Dropdown */}
      <div
        className={`md:hidden flex flex-col space-y-3 px-4 text-gray-700 rounded-b-2xl bg-white overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? "max-h-screen py-3" : "max-h-0 py-0"
        }`}
      >
        {links.map(({ path, label }) => (
          <NavLink
            key={path}
            to={`/${path}`}
            className={(props) =>
              linkClasses(props) + " rounded-xl p-3 hover:bg-gray-100 text-base"
            }
            onClick={() => setIsOpen(false)}
          >
            {label}
          </NavLink>
        ))}

        {/* Mobile last read (compact) */}
        <div className="px-1">
          <LastReadButton
            className={"rounded-xl p-3 hover:bg-gray-100 block"}
          />
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
